'use client'

import { useUser } from '@/hooks/useUser'
import { useAuth } from '@/context/AuthContext'
import { useNavigate } from 'react-router-dom'
import { ReactNode } from 'react'

interface LayoutProps {
  children: ReactNode
  title?: string
  showNav?: boolean
}

export default function Layout({ children, title, showNav = true }: LayoutProps) {
  const { user, loading } = useUser()
  const { logout } = useAuth()
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    navigate('/auth')
    return null
  }

  const handleLogout = () => {
    // Use centralized logout from AuthContext
    logout()
  }

  // Get user display name - check firstName/lastName first, then kyc_data, then email
  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`
    }
    if (user?.kyc_data?.firstName && user?.kyc_data?.lastName) {
      return `${user.kyc_data.firstName} ${user.kyc_data.lastName}`
    }
    return user?.email || 'User'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      {showNav && (
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <h1 className="text-xl sm:text-2xl font-bold text-blue-600">LandLordNoAgent</h1>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600 hidden sm:block">
                  Welcome, {getUserDisplayName()}
                </span>
                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {title && (
          <div className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{title}</h2>
          </div>
        )}
        {children}
      </main>
    </div>
  )
} 

